import React, { useContext, useState } from 'react'
import { AIResultDisplayModel, AIResultViewModel, SegmentedResultViewModel, StructuredSummaryResultViewModel } from './AIResultMapper'
import { AIFeatureType, ComplexAIResultType, SegmentMetadata } from '@zocks-communications/client-sdk-js'
import { ActionableAIResultProps, RoomSessionContext } from './models'
import { Toast, ToastContainer } from 'react-bootstrap'
import { TypedAIResult } from './AIResultsViews'
import classNames from 'classnames'
import { getAIResultTrackingDetails, getRoomAndAIFeatureTrackingDetails } from './tracking'
import { useTracking, TrackEvent } from './common/tracking'
import { AIContextContext } from './state/context'

export const CopyActionAIResult: React.FC<ActionableAIResultProps> = ({
  aiFeature,
  aiResultDisplay,
  onDismissAIResult,
  totalSegmentations,
  onAIResultChanged,
  executionType,
  onSelectAIResult,
}) => {
  const [showToast, setShowToast] = useState(false)
  const { tracked } = useTracking()
  const roomSession = useContext(RoomSessionContext)
  const {
    state: { aiFeatureIdInEdit },
  } = useContext(AIContextContext)
  const onCopyActionAIResult = tracked(
    async (aiResult: AIResultDisplayModel) => {
      await navigator.clipboard.writeText(mapToText(aiResult))
      setShowToast(true)
    },
    {
      eventName: TrackEvent.AI_RESULT_COPIED,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        executionType,
        actionMethod: 'single',
      },
    },
    {
      eventDetailsExtractor: getAIResultTrackingDetails,
    },
  )

  return (
    <div
      className={classNames('ai-result-wrapper ai-result-copy-actions-wrapper', {
        'ai-result-content--selected': aiResultDisplay.selected,
      })}
    >
      <ToastContainer position='top-center' className='p-3 summary-toast'>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={1500} autohide>
          <Toast.Body>Copied to clipboard!</Toast.Body>
        </Toast>
      </ToastContainer>
      <div className='ai-result-actions parent'>
        {!aiFeatureIdInEdit ? (
          <>
            <div
              className='cursor-pointer'
              onClick={async () => {
                await onCopyActionAIResult(aiResultDisplay)
              }}
            >
              <i className='bi bi-copy' />
            </div>

            {onDismissAIResult ? (
              <div
                className='cursor-pointer'
                onClick={() => {
                  onDismissAIResult(aiResultDisplay)
                }}
              >
                {aiResultDisplay.dismissed ? <i className='bi bi-check2' /> : <i className='bi bi-x' />}
              </div>
            ) : null}
          </>
        ) : (
          <div
            className='cursor-pointer'
            onClick={() => {
              onSelectAIResult && onSelectAIResult(aiResultDisplay)
            }}
          >
            {aiResultDisplay.selected ? <i className='bi bi-dash-circle' /> : <i className='bi bi-plus-circle' />}
          </div>
        )}
      </div>

      <div
        className={classNames('ai-result-content', {
          'ai-result-content--dismissed': aiResultDisplay.dismissed,
        })}
      >
        <TypedAIResult
          aiFeature={aiFeature}
          aiResultDisplay={aiResultDisplay}
          totalSegmentations={totalSegmentations}
          onCopyAction={onCopyActionAIResult}
          onDismissAction={onDismissAIResult}
          onAIResultChanged={onAIResultChanged}
          onSelectAIResult={onSelectAIResult}
        />
      </div>
    </div>
  )
}

export const createCopyFeatureResults = (featureAIResults: AIResultViewModel[], roomSessionStartedAt: string) => {
  return featureAIResults
    .map((aiResult) => {
      const totalSegmentations =
        aiResult.result && aiResult.result.type === ComplexAIResultType.SEGMENTED
          ? Math.max(
              ...aiResult.result.segmentResults.map((segmentResult) => {
                return segmentResult.metadata.segmentIdx
              }),
            ) + 1
          : 0
      if (aiResult.result && aiResult.result.type === ComplexAIResultType.SEGMENTED) {
        const typedAIResult = aiResult.result as SegmentedResultViewModel
        return typedAIResult.segmentResults
          .map((segmentResult) => {
            const displayResults = segmentResult.results.map((r) => r.result)
            return createCopyAllText(displayResults.flat(), segmentResult.metadata, totalSegmentations, roomSessionStartedAt)
          })
          .flat()
          .join('\n\n')
      }
      if (aiResult.result && aiResult.result.type === AIFeatureType.STRUCTURED_SUMMARY) {
        const typedAIResult = aiResult.result as StructuredSummaryResultViewModel
        return createCopyAllText(typedAIResult.result, null, totalSegmentations, roomSessionStartedAt)
      }
      return ''
    })
    .join('\n\n')
}

export const createCopyAllText = (
  aiResultDisplays: AIResultDisplayModel[],
  metadata: SegmentMetadata | null,
  totalSegmentations: number,
  roomSessionStartedAt: string,
) => {
  const getTimeFromTimestamp = (transcriptTime: string) => {
    const startedAt = new Date(roomSessionStartedAt).getTime()
    return new Date(parseFloat(transcriptTime) * 1000 - startedAt).toISOString().slice(11, 19)
  }

  const aiResultsToCopy = aiResultDisplays
    .filter((display) => !display.dismissed)
    .map((display) => {
      return mapToText(display)
    })
    .join('\n')

  if (metadata) {
    const topic = metadata.topic ? ` - Topic: ${metadata.topic}` : ''
    const headerCopy = `${getTimeFromTimestamp(metadata.startTime.toString())}-${getTimeFromTimestamp(metadata.endTime.toString())}${topic} - Segment: ${
      metadata.segmentIdx + 1
    } / ${totalSegmentations}`
    return `${headerCopy}\n${aiResultsToCopy}`
  }
  return aiResultsToCopy
}

export const mapToText = (result: AIResultDisplayModel | AIResultDisplayModel[]): string => {
  if (Array.isArray(result)) {
    return result
      .filter((r) => !r.dismissed)
      .map((r) => {
        return `${mapToText(r)}`
      })
      .join('\n')
  }

  if (typeof result.result === 'object') {
    return Object.entries(result.result)
      .map(([key, value]) => {
        return `\n${key}\n${mapToText(value)}`
      })
      .join('\n\n')
  }

  return result.result as string
}
